import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { openClose } from '@core/animations/animations';
import {
	BusinessTitleInsuranceSettings,
	DefaultDOASettings,
} from '@models/businessSettings/settings';
import { MatterMortgage } from '@models/mortgage';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { MatterRepo } from '@repos/matter/matter.repo';
import { BusinessState } from '@state/business';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterMortgagesState } from '@state/matter/matter_mortgages';
import { ICON } from '@core/util/icon';
import {
	MatterManualTitleInsurance,
	TitleInsuranceCompanyName,
	TitleInsuranceProvider,
} from '@models/title-insurance';
import LogRocket from 'logrocket';
import { Matter } from '@models/matter';
import { PropertyUsage } from '@models/title-insurance';
import { select } from '@ngxs/store';

@Component({
	selector: 'app-add-title-insurance',
	templateUrl: './add-title-insurance.component.html',
	styleUrls: ['./add-title-insurance.component.scss'],
	animations: [openClose()],
	standalone: false,
})
export class AddTitleInsuranceComponent implements OnInit {
	_selectedType: TitleInsuranceProvider;
	originalType: TitleInsuranceProvider;
	PropertyUsage = PropertyUsage;
	changeType = false;

	public types = TitleInsuranceProvider;
	public submitted = false;
	public ICON = ICON;
	public loading = false;
	public showManualProviders = false;
	public propertyUsage: PropertyUsage;

	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@SelectSnapshot(MatterMortgagesState.getAllMortgages)
	mortgages: MatterMortgage[];
	@SelectSnapshot(BusinessState.getDefaultTitleInsuranceSettings)
	titleInsuranceSettings: BusinessTitleInsuranceSettings;

	@SelectSnapshot(SelectedMatter.getTitleInsurance)
	titleInsurance: MatterManualTitleInsurance;
	@SelectSnapshot(SelectedMatter.getSelected) matter: Matter;
	businessSettings_ = select(BusinessState.getDefaultSettings);

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: { type: TitleInsuranceProvider },
		private dialogRef: MatDialogRef<AddTitleInsuranceComponent>,
		private matterRepo: MatterRepo,
	) {}

	async ngOnInit(): Promise<void> {
		if (!this.titleInsurance && this.titleInsuranceSettings.defaultProvider) {
			this.loading = true;
			try {
				await this.create(this.titleInsuranceSettings.defaultProvider);
			} catch (error) {
				LogRocket.error(error);
			} finally {
				// do not close here, we need to move them to the select provider screen
				this.loading = false;
			}
		}

		if (
			this.matter.propertyDetails?.propertyType?.usage ===
			PropertyUsage.COMMERCIAL
		) {
			this.propertyUsage = PropertyUsage.COMMERCIAL;
			this.showManualProviders = true;
		}
	}

	async save() {
		this.submitted = this._selectedType !== TitleInsuranceProvider.MANUAL;
		if (this._selectedType) {
			if (!this.originalType || this.originalType !== this._selectedType) {
				const companyName = this.showManualProviders
					? TitleInsuranceCompanyName[this._selectedType]
					: null;
				let selectedType = this._selectedType;
				if (this.showManualProviders) {
					if (
						this.matter?.propertyDetails?.propertyType?.usage ===
							PropertyUsage.RESIDENTIAL ||
						this._selectedType !== TitleInsuranceProvider.FCT
					) {
						selectedType = TitleInsuranceProvider.MANUAL;
					}
				}
				await this.create(selectedType, companyName);
			}
		}
	}

	async create(provider: TitleInsuranceProvider, companyName?: string) {
		if (provider === TitleInsuranceProvider.MANUAL && !companyName) {
			this.showManualProviders = true;
			this._selectedType = null;

			return;
		}

		// If the business setting is set to show counsel credit, we will set the type to FEE
		const showCounselCredit =
			this.businessSettings_()?.titleInsurance?.showCounselCredit ||
			this.titleInsuranceSettings?.showCounselCredit;

		const newTitleInsurance = {
			provider,
			companyName: companyName || TitleInsuranceCompanyName[provider],
			directionOfAmount:
				this.titleInsuranceSettings.directionOfAmount || DefaultDOASettings,
			extendCreditToClient:
				this.titleInsuranceSettings?.extendConnectingCounsel,
			combineCounselFee: this.titleInsuranceSettings?.combineCounselFee,
			showCounselCredit: showCounselCredit,
		} as MatterManualTitleInsurance;

		if (
			provider === TitleInsuranceProvider.MANUAL ||
			this.matter?.propertyDetails?.propertyType?.usage ===
				PropertyUsage.COMMERCIAL
		) {
			newTitleInsurance.effectiveDate = this.matter?.closingDate;
		}

		await this.matterRepo.Update(this.matterId, {
			titleInsurance: newTitleInsurance,
		});

		this.dialogRef.close();
	}

	close() {
		this.dialogRef.close();
	}
}
